import React from 'react';
import {action, computed, observable, reaction, runInAction} from 'mobx';
import {FormattedMessage} from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import unionBy from 'lodash/unionBy';
import cloneDeep from 'lodash/cloneDeep';
import request from '../utils/request';
import {MANAGE_STUDENT, MANAGE_STUDENTS, MANAGE_TEACHERS, PROJECT, SCHOOL} from '../constants/urlConstants';
import imgDefaultAvatar from '../assets/images/avatars/1.jpg';
import {category, events, Roles, StudentStatus, UserStatus} from '../constants/enums';
import mainStore from '../containers/main/store';
import looperStore from '../containers/create/looper/store';
import projectStore from '../containers/main/project/store';
import notificationStore from './NotificationStore';
import schoolStore from '../containers/main/schools/store';
import appStore from '../containers/app/store';
import {fakeStudents} from '../constants/constants';
import {getDefaultAvatar, isDemoStudent} from "../utils/userUtility";
import {customEvent} from "../utils/analyticsUtil";

class ManageStore {
    @observable isProcessing = false;
    @observable studentsList = [];
    @observable teachersList = [];
    @observable isFetchingTeachers = false;
    @observable unassignedStudentsList = [];
    @observable prevAssigned = [];
    @observable studentsInGroup = [];
    @observable search = '';
    @observable isPublic = false;
    @observable isChangedPublic = false;
    @observable editedLessonId = null;
    @observable isChecked = false;
    @observable isSelectedAll = false;
    @observable activeUserChat = null;

    constructor() {
        // setTimeout is a workaround for wierd bug in jest/mobx/whatever
        setTimeout(() => {
            reaction(() => this.studentsList, () => {
                const realStudents = this.studentsList.filter(student => student.status !== StudentStatus.Demo && student.status !== StudentStatus.Fake)
                const activeStudents = realStudents.filter(student => student.status === StudentStatus.Activated);
                customEvent(events.UpdateStudentActivity, {totalNumStudents: realStudents.length, numActiveStudents: activeStudents.length});
            })
        })
    }

    @computed get enrichedStudentList() {
        return [...this.studentsList].map(student => {
            let up = notificationStore.userPresence.find(up => student.id === up.id) || {};
            return {...student, isOnline: up.isOnline, lastActive: up.isOnline ? Date.now() : student.lastLogin}
        }).sort((a, b) => (a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
    }

    @computed get numStudents() {
        return this.enrichedStudentList.length;
    }

    @computed get numTeachers() {
        return this.teachersList.length;
    }

    @computed
    get sortedStudentList() {
        return this.enrichedStudentList.sort((a, b) => {
            if (a.created || b.created) return b.created - a.created;
            if (a.status === StudentStatus.Demo) return -1;
            if (b.status === StudentStatus.Demo) return 1;
            return (a.name || '').toLowerCase() > (b.name || '').toLowerCase() ? 1 : -1;
        });
    }

    @computed
    get recentStudents() {
        const liveStudents = this.enrichedStudentList.filter(s => s.status !== StudentStatus.Demo && s.status !== StudentStatus.Inactive);
        const sortedStudents = liveStudents.sort((a, b) => b.created - a.created);
        return [...sortedStudents, ...fakeStudents].slice(0, 3);
    }

    @computed
    get noAssignedList() {
        return this.unassignedStudentsList.filter((s) => {
            const isActive = StudentStatus.Inactive !== s.status && StudentStatus.Demo !== s.status;
            return isActive && `${s.name} ${s.email}`.toLowerCase().includes(this.search);
        });
    }

    @computed
    get isChangedAssigned() {
        return this.unassignedStudentsList.some(s => s.selected) || this.isChangedPublic;
    }

    @computed
    get students() {
        return this.enrichedStudentList.filter((s) => {
            return `${s.name} ${s.email} ${s.phone}`.toLowerCase().includes(this.search);
        });
    }

    @computed
    get hasVideoMeetingUrl() {
        return true;
    }

    @computed
    get showDiscover() {
        if (schoolStore.hideDiscover) {
            return !schoolStore.hideDiscover;
        }
        return this.teachersList.filter(teacher => teacher.role !== Roles.Admin).every(teacher => {
            return !teacher.hideStudentDiscover;
        })
    }

    @computed
    get hasActivePayment() {
        if (this.teachersList.length === 0) {
            return true;
        }
        return this.teachersList.some(teacher => {
            return teacher.userStatus !== UserStatus.TrialEnded && teacher.role !== Roles.Admin;
        })
      }

    @computed
    get activeStudents() {
        return this.enrichedStudentList.filter(s => s.status !== StudentStatus.Fake);
    }

    @computed
    get liveStudentsLength() {
        return this.enrichedStudentList.filter(s => ![StudentStatus.Inactive, StudentStatus.Demo, StudentStatus.Fake].includes(s.status)).length;
    }

    @computed
    get filteredActiveStudents() {
        const students = this.activeStudents;
        return students.filter(s => (
            `${s.name} ${s.email}`.toLowerCase().includes(this.search)
        ));
    }

    @computed
    get sortedStudentData() {
        const liveStudents = this.enrichedStudentList.filter(s => s.status !== StudentStatus.Demo && s.status !== StudentStatus.Inactive);
        liveStudents.sort((a, b) => {
            let aLastActive = Math.max(a.lastActive || 0, a.created);
            let bLastActive = Math.max(b.lastActive || 0, b.created);
            return bLastActive - aLastActive
        });

        if (liveStudents.length <= 1 && !isEmpty(this.molly)) {
            return {
                list: liveStudents,
                fake: true
            }
        }
        return {
            list: liveStudents,
            fake: false
        }
    }

    @computed
    get demoStudentId() {
        const demoStudentIndex = this.studentsList.findIndex(student => student.role === StudentStatus.Demo);
        return (demoStudentIndex !== -1 ? this.studentsList[demoStudentIndex].id : -1);
    }

    @computed
    get molly() {
        const demoStudentIndex = this.studentsList.findIndex(student => {
            return isDemoStudent(student.name);
        });
        const molly = {...(demoStudentIndex !== -1 ? this.studentsList[demoStudentIndex] : null)};

        if (!isEmpty(molly)) {
            molly.status = 2;
        }

        return molly;
    }

    @computed
    get filteredStudentsInGroup() {
        return this.studentsInGroup.filter((student) => {
            return `${student.name} ${student.email}`.toLowerCase().includes(this.search);
        }).sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
    }

    @computed
    get selectedStudentsInGroup() {
        return this.studentsInGroup.reduce((total, student) => {
            if (student.selected) total += 1;
            return total;
        }, 0);
    }

    @computed
    get checkIsSelectedAll() {
        return this.studentsInGroup.every(s => s.selected);
    }

    @action setAssignedData = async (id, list, isPublic) => {
        await this.fetchStudentList();
        runInAction(() => {
            this.prevAssigned = list.map(s => ({...s, selected: true, assigned: true}));
            this.unassignedStudentsList = this.enrichedStudentList
                .map(s => ({...s, selected: false}))
                .filter(s => !list.some(stud => stud.id === s.id));
            this.isChangedPublic = false;
            this.isPublic = isPublic;
            this.editedLessonId = id;
        });
    };

    @action setStudentsInGroup = async (students) => {
        await this.fetchStudentList();
        const currentStudents = students.map(s => ({...s, selected: true}));
        const newStudents = this.studentsList
            .map(s => ({...s, selected: false}))
            .filter(student => (![StudentStatus.Inactive, StudentStatus.Demo].includes(student.status)));
        runInAction(() => {
            this.studentsInGroup = cloneDeep(unionBy(currentStudents, newStudents, 'id'));
            this.isChecked = false;
            this.isSelectedAll = this.checkIsSelectedAll;
        });
    };

    getNumStudents = async () => {
        await this.fetchStudentList();
        return this.studentsList.length;
    };

    @action fetchStudentList = async (force) => {
        if ((this.isProcessing || (!isEmpty(this.studentsList) && !force))) {
            return;
        }
        if (schoolStore.activeSchool && schoolStore.activeSchool.students) {
            if (force) {
                await schoolStore.fetchSchool(schoolStore.activeSchool.id, false);
            }
            runInAction(() => {
                this.studentsList = schoolStore.students;
                this.isProcessing = false;
            })
            return;
        }

        return this.fetchStudents();
    }

    @action fetchStudents = async (teacherUsername) => {
        this.isProcessing = true;
        try {
            const query = teacherUsername ? `?teacherUsername=${encodeURIComponent(teacherUsername)}` : '';
            let studentsList = await request(`${MANAGE_STUDENTS}${query}`);
            runInAction(() => {
                this.studentsList = studentsList
                    .filter(student => student)
                    .map((student) => {
                        return {
                            ...student,
                            selected: false,
                            name: student.name || '',
                            phone: student.phone || '',
                            status: student.status,
                            note: student.note || '',
                            avatar: student.avatar || imgDefaultAvatar,
                            created: student.created,
                            lastActive: student.lastLogin,
                            theme: student.theme
                        };
                    });
                this.isProcessing = false;
            });
        } catch (e) {
            console.log(e);
            runInAction(() => {
                this.isProcessing = false;
            });
        }
    };

    @action fetchStudentListForSpecificTeacher = async (teacherUsername) => {
        if (this.isProcessing) {
            return;
        }
        return this.fetchStudents(teacherUsername);
    }

    @action updateStudentStatus = (id, email, status, givenName, familyName, avatar) => {
        // TODO: improve just like the example from updateStudentTheme
        this.studentsList = this.studentsList.map(student => student.id === id ? {
            ...student,
            email,
            status: status || 0,
            name: [givenName, familyName].join(' ').trim(),
            avatar: avatar || getDefaultAvatar()
        } : student);
    };

    @action updateStudentTheme = (id, theme) => {
        const student = this.getStudent(id)
        student.theme = theme
    };

    @action updateStudentAvatar = (id, avatar) => {
        const student = this.getStudent(id)
        student.avatar = avatar
    };

    @action updateList = ({email, givenName, familyName, id, avatar, instruments, role, status}) => {
        let list = role === Roles.Student ? this.studentsList : this.teachersList;
        const existingStudentIndex = list.findIndex(student => student.id === id);
        const name = this.getFullName(givenName, familyName);
        if (existingStudentIndex > -1) {
            list[existingStudentIndex] = Object.assign(list[existingStudentIndex], {email, name, givenName, familyName, id, avatar, instruments})
            return true;
        }
        const newStudent = this.generateNewUser(email, givenName, familyName, id, avatar, instruments, role, status);
        let newList = [newStudent, ...list];
        if (role === Roles.Student) {
            this.studentsList = newList;
        } else if (role === Roles.Teacher) {
            this.teachersList = newList;
        }
        return false;
    };


    @action clearTemporaryAtrrs = () => {
        this.studentsList = this.studentsList.map(({generated, ...keepAttrs}) => keepAttrs);
    };

    @action removeUser = async ({id, email, role}) => {
        let body = {userId: id, email};
        if (!id) {
            body = {email};
        }
        this.isProcessing = true;
        mainStore.toggleProgressLoader(true);
        try {
            let url;
            let list;
            if (role === Roles.Student) {
                url = MANAGE_STUDENT;
                list = this.studentsList;
            } else if (role === Roles.Teacher && schoolStore.activeSchool && schoolStore.activeSchool.id) {
                url = `${SCHOOL}/${schoolStore.activeSchool.id}/teachers`;
                list = this.teachersList;
            }
            await request(url, {method: 'DELETE', body});
            customEvent(events.CustomEvent, {category: category.StudentRemoved})
            runInAction(() => {
                const index = list.findIndex(s => s.id === id);
                list.splice(index, 1);
            });
        } catch (err) {
            console.warn(err);
        } finally {
            runInAction(() => {
                mainStore.toggleProgressLoader(false);
                this.isProcessing = false;
            });
        }
    };

    @action updateStudentPhone = async (id, phone) => {
        if (!id) {
            return;
        }
        this.isProcessing = true;
        const index = this.studentsList.findIndex(s => s.id === id);
        const currentPhone = this.studentsList[index].phone;
        this.studentsList[index].phone = phone;
        try {
            await request(`${MANAGE_STUDENT}/${id}/phone`, {method: 'POST', body: {phone}});
        } catch ({err}) {
            runInAction(() => {
                this.studentsList[index].phone = currentPhone;
                throw new Error(err);
            });
        } finally {
            runInAction(() => {
                this.isProcessing = false;
            });
        }
    };

    @action updateNote = async (id, note, isTeacher) => {
        if (!id) {
            return;
        }
        mainStore.toggleProgressLoader(true);
        let list = isTeacher ? this.teachersList : this.studentsList;
        const index = list.findIndex(s => s.id === id);
        try {
            await request(`${MANAGE_STUDENT}/${id}/note`, {method: 'POST', body: {note}});
            runInAction(() => {
                list[index] = Object.assign({}, list[index], {note});
                mainStore.toggleProgressLoader(false);
            });
        } catch (e) {
            appStore.createToast({
                message: <FormattedMessage id="general.error.note"/>,
                duration: 3000,
                className: 'left-offset-43'
            });

            mainStore.toggleProgressLoader(false);
        }
    };

    @action searchStudents = (val) => {
        this.search = val.toLowerCase();
    };

    @action toggleSelectedStudent = (id, value) => {
        let index = this.unassignedStudentsList.findIndex(s => s.id === id);
        if (index < 0) {
            index = this.prevAssigned.findIndex(s => s.id === id);
            this.prevAssigned[index].selected = value;
        } else {
            this.unassignedStudentsList[index].selected = value;
        }
    };

    @action toggleAssignedStudent = (student) => {
        student.selected = !student.selected;
        this.isChecked = true;
        this.isSelectedAll = this.checkIsSelectedAll;
    };

    @action toggleIsPublicLesson = () => {
        this.isChangedPublic = !this.isChangedPublic;
        this.isPublic = !this.isPublic;
        this.unassignedStudentsList = this.unassignedStudentsList.map(s => ({...s, selected: this.isPublic}));
        this.prevAssigned = this.prevAssigned.map(s => ({...s, selected: this.isPublic}));
    };

    @action toggleIsSelectedAll = () => {
        this.isSelectedAll = !this.isSelectedAll;
        this.isChecked = true;
        this.studentsInGroup = this.studentsInGroup.map(s => ({...s, selected: this.isSelectedAll}));
    };

    isExistStudent = (email) => {
        const emailLowerCase = email.toLowerCase();
        return this.studentsList.findIndex(s => s.email === emailLowerCase) !== -1;
    };

    sendAssignedStudents = async (lessonId, studentList) => {
        const body = {
            assignedUsers: studentList
                .filter(item => item.selected && item.id && item.status !== StudentStatus.Demo)
                .map(({id, name, avatar, assignedDate}) => ({id, name, avatar, assignedDate: assignedDate || new Date()})),
            assignedToAll: false
        };
        const resp = await request(`${PROJECT}/${lessonId}/assign`, {method: 'POST', body});
        projectStore.onStudentsAssigned(resp.id, resp.assignedUsers || [], resp.assignedToAll);
        looperStore.onStudentsAssigned(resp.id, resp.assignedUsers || [], resp.assignedToAll);
        return resp;
    };

    sendAttachedStudents = async (teacherId, studentList) => {
        mainStore.toggleProgressLoader(true);
        try {
            const body = studentList;
            const resp = await request(`${SCHOOL}/teachers/${teacherId}`, {method: 'POST', body});
            runInAction(() => {
                let teacherObj = this.getTeacher(teacherId);
                if (teacherObj.students) {
                    teacherObj.students.replace(studentList);
                } else {
                    teacherObj.students = studentList;
                }
                //todo really really ugly
                const studentsMap = studentList.reduce((res, student) => {
                    res[student] = true;
                    return res;
                }, {});
                this.studentsList.replace(this.studentsList.map(student => {
                    const idx = student.teachers.findIndex(id => id === teacherId);
                    if (idx >= 0) {
                        if (!studentsMap[student.id]) {
                            student.teachers.splice(idx, 1);
                        }
                    } else if (studentsMap[student.id]) {
                        student.teachers.push(teacherId);
                    }
                    return student;
                }));
                return teacherObj;
            });
        } finally {
            mainStore.toggleProgressLoader(false);
        }
    };

    getFullName = (givenName, familyName) => {
        return [givenName, familyName].join(' ').trim();
    }

    generateNewUser = (email, givenName, familyName, id, avatar = imgDefaultAvatar, instruments, role, status) => {
        return {
            avatar,
            name: this.getFullName(givenName, familyName),
            givenName,
            familyName,
            email,
            id,
            instruments,
            phone: '',
            note: '',
            role: role,
            created: Date.now(),
            status: status || (email ? StudentStatus.Invited : StudentStatus.NotInvited),
            teachers: []
        };
    };

    @action checkIfTeachersAreOnline = () => {
        this.teachersList.map(teacher => {
            notificationStore.sendPresencePing(teacher.id);
        });
    }

    @action fetchTeachersList = async (force = false) => {
        if ((this.isFetchingTeachers || !isEmpty(this.teachersList)) && !force) {
            return;
        }
        if (schoolStore.activeSchool && schoolStore.activeSchool.teachers) {
            this.teachersList = schoolStore.teachers.concat(schoolStore.admins)
            this.isFetchingTeachers = false;
            return;
        }
        this.isFetchingTeachers = true;
        try {
            let teachersList = await request(`${MANAGE_TEACHERS}`);

            runInAction(() => {
                this.teachersList = teachersList;
                this.checkIfTeachersAreOnline();
                clearInterval(this.interval);
                this.interval = setInterval(() => {
                    this.checkIfTeachersAreOnline()
                }, 1000 * 60);
            });
        } catch (e) {
            console.log(e);
        } finally {
            runInAction(() => {
                this.isFetchingTeachers = false;
            });
        }
    };

    @computed
    get teachers() {
        return this.teachersList.filter(t => t.role === Roles.Teacher) || [];
    }

    @computed
    get teachersWithStatus() {
        return this.teachersList.map(teacher => {
            teacher.isOnline = notificationStore.isUserOnline(teacher.id);
            return teacher;
        }) || [];
    }

    @computed
    get sortedTeachers() {
        return this.teachers.sort((a, b) => {
            return (a.name || '').toLowerCase() > (b.name || '').toLowerCase() ? 1 : -1;
        });
    }

    @computed
    get searchedTeachers() {
        return this.sortedTeachers.filter((s) => {
            return `${s.name} ${s.email}`.toLowerCase().includes(this.search);
        });
    }

    @computed
    get numOfTeachers() {
        return this.teachers.length;
    }

    getTeacher = (teacherId) => {
        return this.teachersList.find(teacher => teacher.id === teacherId);
    };

    @computed
    get hasPayingStudent() {
        return this.studentsList.some(s => s.status === StudentStatus.Activated);
    }

    @action activateStudent = async (studentId) => {
        if (!studentId) {
            return;
        }
        this.isProcessing = true;
        try {
            await request(`${MANAGE_STUDENT}/${studentId}/field`, {method: 'POST', body: {name: 'custom:user_status', value: UserStatus.Active}});
            const index = this.studentsList.findIndex(s => s.id === studentId);
            runInAction(() => {
                this.studentsList[index].status = StudentStatus.Activated;
            });
        } catch ({err}) {
            runInAction(() => {
                throw new Error(err);
            });
        } finally {
            runInAction(() => {
                this.isProcessing = false;
            });
        }
    }

    isConnectedStudent = (userId) => {
        const student = this.getStudent(userId);
        return student && !!student.email;
    }

    getStudent = (studentId) => {
        return this.studentsMap[studentId];
    }

    @computed get studentsMap() {
        let map = {};
        this.studentsList.forEach(student => map[student.id] = student);
        return map;
    }

}

const manageStore = new ManageStore();
export default manageStore;
